import React, { ComponentProps, useCallback, useState } from 'react';
import { defineMessages, FormattedMessage, type MessageValue, useIntl } from 'react-intl';
import type { NoMessageValues } from 'react-intl';
import { StyleSheet } from 'react-native';

import { BodyText, Image, theme, useResponsive } from '@sg/garnish';
import { AllergenProperty } from '@sg/graphql-schema';
import { type AllergenTypes } from '@sg/shared-fe-features-menu';

import { HomeProductCardPressable } from '../HomeProductCardPressable';
import {
  HomeProductImage,
  HOME_PRODUCT_IMAGE_SIZE_XS_REGULAR,
  HOME_PRODUCT_IMAGE_SIZE_XS_LARGE,
  HOME_PRODUCT_IMAGE_SIZE_SM,
  HOME_PRODUCT_IMAGE_SIZE_LG,
  HOME_PRODUCT_IMAGE_SIZE_XL,
} from '../HomeProductImage';
import { HomeProductInfo } from '../HomeProductInfo';

import type { HomeProductCardSize } from './HomeProductCard.types';

export {
  HOME_PRODUCT_CARD_SIZES_REGULAR,
  HOME_PRODUCT_CARD_SIZES_LARGE,
} from '../HomeProductImage';

// ────────────────────────────────────────────────────────────────────────────

export const HomeProductCard = (props: HomeProductCardProps) => {
  const {
    categoryName,
    isEnriched,
    withMaxWidth = false,
    size = 'regular',
    id,
    slug,
    isModifiable,
    title = '',
    description,
    deepLink,
    cost,
    fallbackImage,
    cloudinaryImage,
    contentfulImage,
    imageA11y,
    openMenu,
    openProductDetails,
    addProductToBag,
    trackItemSelection,
  } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint, match } = useResponsive();

  // ─── Sizing ────────────────────────────────────────────────────────────────

  const titleSizeXS = size === 'regular' ? ('14' as const) : ('24' as const);
  const bodySizeXS = size === 'regular' ? ('12' as const) : ('16' as const);
  const maxWidthXS = size === 'regular' ? styles.maxWidthXSRegular : styles.maxWidthXSLarge;
  const containerXS = size === 'regular' ? styles.containerXSRegular : styles.containerXSLarge;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const maxWidthStyle = match([maxWidthXS, styles.maxWidthSM, styles.maxWidthLG, styles.maxWidthXL]); // prettier-ignore
  const containerStyle = match([containerXS, styles.containerSM, styles.containerLG, styles.containerXL]); // prettier-ignore
  const widthStyle = withMaxWidth ? maxWidthStyle : null;

  // ─── A11y ──────────────────────────────────────────────────────────────────

  const a11yLabel = formatMessage(
    isEnriched && !isModifiable ? messages.addToBagA11yLabel : messages.viewProductA11yLabel,
    { product_name: title },
  );

  // ─── State ─────────────────────────────────────────────────────────────────

  const [isAddingToBag, setIsAddingToBag] = useState(false);

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const handleLinkPress = useCallback(() => {
    trackItemSelection(title, categoryName);
  }, [categoryName, title, trackItemSelection]);

  const handlePress = useCallback(async () => {
    trackItemSelection(title, categoryName);

    // Products from contentful that aren't enriched with a menu yet open the location selection instead.
    if (!isEnriched) {
      openMenu();
      return;
    }

    if (isModifiable) {
      openProductDetails(slug);
      return;
    }

    setIsAddingToBag(true);
    await addProductToBag(id, title);
    setIsAddingToBag(false);
  }, [
    isEnriched,
    categoryName,
    id,
    slug,
    title,
    isModifiable,
    openProductDetails,
    addProductToBag,
    openMenu,
    trackItemSelection,
  ]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <HomeProductCardPressable
      accessibilityLabel={a11yLabel}
      deepLink={deepLink}
      style={[containerStyle, widthStyle]}
      onPress={deepLink ? handleLinkPress : handlePress}
    >
      <HomeProductImage
        size={size}
        cost={cost}
        isModifiable={isModifiable}
        isAddingToBag={isAddingToBag}
        cloudinaryImage={cloudinaryImage}
        contentfulImage={contentfulImage}
        fallbackImage={fallbackImage}
        imageA11y={imageA11y ?? title}
      />

      {title ? (
        <BodyText
          style={match([styles.titleXS, styles.titleSM])}
          sizeMatch={[titleSizeXS, '18']}
          bold
        >
          {title}
        </BodyText>
      ) : null}

      {description ? (
        <BodyText
          sizeMatch={[bodySizeXS, '14']}
          style={match([styles.descriptionXS, styles.descriptionSM])}
          numberOfLines={match([2, 3])}
        >
          {description}
        </BodyText>
      ) : null}

      <HomeProductInfo {...props} />

      {size === 'large' && currentBreakpoint.isXS ? (
        <BodyText sizeMatch={['18']} style={styles.orderNow}>
          <FormattedMessage {...messages.orderNow} />
        </BodyText>
      ) : null}
    </HomeProductCardPressable>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXSRegular: {
    flex: 1,
    gap: theme.spacing['2'],
    minWidth: HOME_PRODUCT_IMAGE_SIZE_XS_REGULAR,
  },
  containerXSLarge: {
    flex: 1,
    gap: theme.spacing['2'],
    minWidth: HOME_PRODUCT_IMAGE_SIZE_XS_LARGE,
  },
  containerSM: {
    flex: 1,
    gap: theme.spacing['2'],
    minWidth: HOME_PRODUCT_IMAGE_SIZE_SM,
  },
  containerLG: {
    flex: 1,
    gap: theme.spacing['2'],
    minWidth: HOME_PRODUCT_IMAGE_SIZE_LG,
  },
  containerXL: {
    flex: 1,
    gap: theme.spacing['2'],
    minWidth: HOME_PRODUCT_IMAGE_SIZE_XL,
  },
  maxWidthXSRegular: {
    maxWidth: HOME_PRODUCT_IMAGE_SIZE_XS_REGULAR,
  },
  maxWidthXSLarge: {
    maxWidth: HOME_PRODUCT_IMAGE_SIZE_XS_LARGE,
  },
  maxWidthSM: {
    maxWidth: HOME_PRODUCT_IMAGE_SIZE_SM,
  },
  maxWidthLG: {
    maxWidth: HOME_PRODUCT_IMAGE_SIZE_LG,
  },
  maxWidthXL: {
    maxWidth: HOME_PRODUCT_IMAGE_SIZE_XL,
  },
  titleXS: {
    textTransform: 'capitalize',
    paddingRight: theme.spacing['2'],
  },
  titleSM: {
    marginTop: theme.spacing['2'],
    textTransform: 'capitalize',
  },
  descriptionXS: {
    color: theme.colors.CHARCOAL,
    paddingRight: theme.spacing['2'],
  },
  descriptionSM: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
  orderNow: {
    marginTop: 'auto',
    backgroundColor: theme.colors.OPACITY.SPINACH.NEAR_LIGHTEST,
    color: theme.colors.KALE,
    textAlign: 'center',
    paddingHorizontal: theme.spacing['6'],
    paddingVertical: theme.spacing['4'],
    borderRadius: 56,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeProductCardProps = {
  categoryName: string;
  isEnriched: boolean;
  withMaxWidth?: boolean;
  size?: HomeProductCardSize;
  id: string;
  slug: string;
  isModifiable: boolean;
  title?: string;
  description?: string;
  deepLink?: string;
  cost?: number;
  fallbackImage?: ComponentProps<typeof Image>['source'];
  cloudinaryImage?: string;
  contentfulImage?: string;
  imageA11y?: string;
  dietaryPreferences: AllergenTypes;
  allergenProperties: readonly AllergenProperty[];
  calories?: number;
  calorieOverrideText?: string;
  totalFatG?: number;
  totalCarbsG?: number;
  dietaryFiberG?: number;
  sugarsG?: number;
  proteinG?: number;
  openMenu: () => void;
  openProductDetails: (slug: string) => void;
  addProductToBag: (id: string, title: string) => Promise<void>;
  trackItemSelection: (productId: string, categoryName: string) => void;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly addToBagA11yLabel: { readonly product_name: MessageValue };
  readonly viewProductA11yLabel: { readonly product_name: MessageValue };
  readonly orderNow: NoMessageValues;
}>({
  addToBagA11yLabel: {
    defaultMessage: 'Add 1 {product_name} to your bag',
    description: 'Home | Product card | Add to bag a11y label',
  },
  viewProductA11yLabel: {
    defaultMessage: 'View {product_name}',
    description: 'Home | Product card | View product a11y label',
  },
  orderNow: {
    defaultMessage: 'Order now',
    description: 'Home | Product card | Order now',
  },
});
